import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Loader2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { API_URL } from '../config';

const API = API_URL;

const UsersListPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = users.filter(user => 
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/super-admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
        setFilteredUsers(data.users || []);
      } else {
        toast.error('Failed to load users');
        navigate('/admin');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Error loading users');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/super-admin/users/export`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success('Users exported successfully');
      } else {
        toast.error('Failed to export users');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Error exporting users');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#0A0A0A' }}>
      {/* Header */}
      <div 
        className="sticky top-0 z-50 border-b"
        style={{ background: '#121212', borderColor: '#2A2A2A' }}
      >
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/admin')}
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="text-white text-xl font-bold">Users Management</h1>
            </div>
            <Button
              onClick={handleExport}
              disabled={exporting || users.length === 0}
              className="bg-[#EE1C4E] hover:bg-[#EE1C4E]/90 text-white flex items-center gap-2"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Export CSV
            </Button>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name or email..."
              className="w-full pl-10 pr-4 py-2 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#EE1C4E]"
              style={{ background: '#1A1A1A', border: '1px solid #2A2A2A' }}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#EE1C4E]" />
          </div>
        ) : (
          <div>
            <div className="mb-4 text-gray-400 text-sm">
              Showing {filteredUsers.length} of {users.length} users
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b" style={{ borderColor: '#2A2A2A' }}>
                    <th className="text-left py-3 px-4 text-gray-400 font-semibold text-sm">Name</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-semibold text-sm">Email</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-semibold text-sm">Type</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-semibold text-sm">Role</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-semibold text-sm">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user, index) => (
                    <tr 
                      key={user.email}
                      className="border-b hover:bg-white/5 transition-colors"
                      style={{ borderColor: '#2A2A2A' }}
                    >
                      <td className="py-3 px-4 text-white">{user.name || 'N/A'}</td>
                      <td className="py-3 px-4 text-gray-300">{user.email}</td>
                      <td className="py-3 px-4">
                        <span 
                          className="px-2 py-1 rounded-full text-xs font-semibold"
                          style={{
                            background: user.is_premium ? '#EE1C4E' : '#2A2A2A',
                            color: 'white'
                          }}
                        >
                          {user.is_premium ? '👑 Premium' : 'Free'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-300 capitalize">{user.role?.replace('_', ' ')}</td>
                      <td className="py-3 px-4 text-gray-400 text-sm">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredUsers.length === 0 && (
              <div className="text-center py-20">
                <p className="text-gray-400 text-lg">No users found</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UsersListPage;
